import { providers } from '../../providers';
import { kie } from '../kie';
import { kie_general } from '../kie_general';
import { baidu } from '../baidu';
import { atlascloud } from '../atlascloud';
import { wavespeed } from '../wavespeed';
import type { VideoModelEntry } from '../types';

// 价格表: 人民币/秒
const VEO31_LITE = { '720p': 0.22, '1080p': 0.36 };
const VEO31_LITE_AUDIO = { '720p': 0.36, '1080p': 0.58 };
const VEO31_FAST = { '720p': 0.58, '1080p': 0.72, '4k': 1.8 };
const VEO31_FAST_AUDIO = { '720p': 0.72, '1080p': 0.86, '4k': 2.16 };
const VEO31 = { '720p': 1.44, '1080p': 1.44, '4k': 2.88 };
const VEO31_AUDIO = { '720p': 2.88, '1080p': 2.88, '4k': 4.32 };
const OMNI_11_FLASH = { '360p': 0.03, '720p': 0.10, '1080p': 0.15, '4k': 0.30 };
// 查找价格函数
const lookup = (table: Record<string, number>, quality?: string) =>
    table[quality ?? ''] ?? Math.max(...Object.values(table));
// 美元汇率
const RMB_TO_USD = 1 / 6.8;
// rmb to usd 转换
const usd = (rmb: number) => rmb * RMB_TO_USD;
// 系数
const COST_FACTOR = 1.5;

export const googleModels: VideoModelEntry[] = [
    {
        id: 'gemini-omni-flash',
        chef: providers.google,
        providers: [
            {
                ...providers.google,
                model: kie('gemini-omni-flash'),
                modelBackup: [atlascloud('gemini-omni-flash')],
                getCost: ({ duration, quality, providerOptions }) => {
                    const frameMode = providerOptions?._frameMode;
                    if (frameMode === 'v2v') {
                        return (quality === '4k' ? 2.4 : 1.6) * COST_FACTOR;
                    }
                    const base = quality === '4k' ? 1 : 0.2;
                    return (base + duration * 0.1) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const frameMode = providerOptions?._frameMode;
                    if (frameMode === 'v2v') {
                        return (quality === '4k' ? 2.4 : 1.6) * COST_FACTOR * 100;
                    }
                    const base = quality === '4k' ? 1 : 0.2;
                    return (base + duration * 0.1) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16'],
                durations: [4, 6, 8, 10],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: ['16:9', '9:16'],
                durations: [4, 6, 8, 10],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 1, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '9:16'],
                durations: [4, 6, 8, 10],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 7, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            v2v: {
                aspectRatios: ['16:9', '9:16'],
                durations: [],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 5, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', minDuration: '1s', maxDuration: '10s', formats: ['video/mp4', 'video/webm'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            ref: ['prompt', 'image'],
            v2v: ['prompt', 'video'],
        },
        supportsEdit: true,
        supportsInput: ['t2v', 'first', 'reference', 'v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'gemini-omni-flash-api',
        chef: providers.google,
        providers: [
            {
                ...providers.google,
                model: atlascloud('gemini-omni-flash-api'),
                modelBackup: [wavespeed('gemini-omni-flash-api')],
                getCost: ({ duration, providerOptions }) => {
                    const frameMode = providerOptions?._frameMode;
                    if (frameMode === 'first') {
                        return duration * 0.13 * COST_FACTOR;
                    } else if (frameMode === 'ref') {
                        return duration * 0.135 * COST_FACTOR;
                    } else if (frameMode === 'v2v') {
                        const inputDur = providerOptions?._media?.videoDurationSeconds ?? duration;
                        const billed = Math.min(30, Math.max(3, inputDur));
                        return billed * 0.14 * COST_FACTOR;
                    } else {
                        return duration * 0.125 * COST_FACTOR;
                    }
                },
                getPassCount: ({ duration, providerOptions }) => {
                    const frameMode = providerOptions?._frameMode;
                    if (frameMode === 'first') {
                        return duration * 0.13 * COST_FACTOR * 100;
                    } else if (frameMode === 'ref') {
                        return duration * 0.135 * COST_FACTOR * 100;
                    } else if (frameMode === 'v2v') {
                        const inputDur = providerOptions?._media?.videoDurationSeconds ?? duration;
                        const billed = Math.min(30, Math.max(3, inputDur));
                        return billed * 0.14 * COST_FACTOR * 100;
                    } else {
                        return duration * 0.125 * COST_FACTOR * 100;
                    }
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['720p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: ['16:9', '9:16'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['720p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 1, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '9:16'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['720p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 5, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            v2v: {
                aspectRatios: [],
                durations: [],
                qualities: ['720p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 5, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', maxDuration: '30s', formats: ['video/mp4', 'video/webm'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            ref: ['prompt', 'image'],
            v2v: ['prompt', 'video'],
        },
        supportsEdit: true,
        supportsInput: ['t2v', 'first', 'reference', 'v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'gemini-omni-1.1-flash',
        chef: providers.google,
        providers: [
            {
                ...providers.google,
                model: wavespeed('gemini-omni-1.1-flash'),
                getCost: ({ duration, quality, providerOptions }) => {
                    const rate = lookup(OMNI_11_FLASH, (quality ?? '720p').toLowerCase());
                    const frameMode = providerOptions?._frameMode;
                    if (frameMode === 'v2v') {
                        const inputDur = (providerOptions?._media?.videoDurationSeconds ?? duration) || 3;
                        const billed = Math.min(10, Math.max(3, Math.ceil(inputDur)));
                        return billed * rate * COST_FACTOR;
                    }
                    return duration * rate * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const rate = lookup(OMNI_11_FLASH, (quality ?? '720p').toLowerCase());
                    const frameMode = providerOptions?._frameMode;
                    if (frameMode === 'v2v') {
                        const inputDur = (providerOptions?._media?.videoDurationSeconds ?? duration) || 3;
                        const billed = Math.min(10, Math.max(3, Math.ceil(inputDur)));
                        return billed * rate * COST_FACTOR * 100;
                    }
                    return duration * rate * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['360p', '720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: ['16:9', '9:16'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['360p', '720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 1, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '9:16'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['360p', '720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 2, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '9:16'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['360p', '720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 10, maxSize: '20MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: true, maxCount: 3, maxSize: '100MB', minDuration: '1s', maxDuration: '3s', formats: ['video/mp4', 'video/webm'] },
                    audio: { allow: false },
                },
            },
            v2v: {
                aspectRatios: [],
                durations: [],
                qualities: ['360p', '720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: false },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', minDuration: '1s', maxDuration: '10s', formats: ['video/mp4', 'video/webm'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image|video'],
            v2v: ['prompt', 'video'],
        },
        supportsEdit: true,
        supportsInput: ['t2v', 'first', 'first_last', 'reference', 'v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'veo3.1-lite',
        chef: providers.google,
        providers: [
            {
                ...providers.google,
                model: kie_general('veo3.1-lite'),
                modelBackup: [baidu('veo3.1-lite')],
                getCost: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generateAudio === true;
                    return usd(duration * lookup(withAudio ? VEO31_LITE_AUDIO : VEO31_LITE, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generateAudio === true;
                    return usd(duration * lookup(withAudio ? VEO31_LITE_AUDIO : VEO31_LITE, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16'],
                durations: [4, 6, 8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [4, 6, 8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '9:16'],
                durations: [4, 6, 8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            }
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'veo3.1-fast',
        chef: providers.google,
        providers: [
            {
                ...providers.google,
                model: kie_general('veo3.1-fast'),
                modelBackup: [baidu('veo3.1-fast')],
                getCost: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generateAudio === true;
                    return usd(duration * lookup(withAudio ? VEO31_FAST_AUDIO : VEO31_FAST, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generateAudio === true;
                    return usd(duration * lookup(withAudio ? VEO31_FAST_AUDIO : VEO31_FAST, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16'],
                durations: [4, 6, 8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [4, 6, 8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '9:16'],
                durations: [4, 6, 8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: [],
                durations: [8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 3, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            }
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'veo3.1',
        chef: providers.google,
        providers: [
            {
                ...providers.google,
                model: kie_general('veo3.1'),
                modelBackup: [baidu('veo3.1')],
                getCost: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generateAudio === true;
                    return usd(duration * lookup(withAudio ? VEO31_AUDIO : VEO31, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generateAudio === true;
                    return usd(duration * lookup(withAudio ? VEO31_AUDIO : VEO31, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16'],
                durations: [4, 6, 8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [4, 6, 8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '9:16'],
                durations: [4, 6, 8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: [],
                durations: [8],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 3, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            }
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
];